import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface SuccessPageProps {
  businessName?: string;
  email?: string;
}

export const SuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { businessName, email } = (location.state as SuccessPageProps) || {};

  useEffect(() => {
    // Fire accountCreated event when page loads
    console.log('Success page loaded, firing accountCreated event');
    
    // Ensure dataLayer exists
    window.dataLayer = window.dataLayer || [];
    
    // Push the event
    window.dataLayer.push({
      event: 'accountCreated',
      timestamp: new Date().toISOString(),
    });
    
    console.log('accountCreated event pushed to dataLayer:', window.dataLayer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4" style={{ fontFamily: 'Outfit' }}>
      <div className="max-w-2xl w-full">
        {/* Success Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        {/* Main Content */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Thank you{businessName ? `, ${businessName}` : ''}!
          </h1>
          <p className="text-xl text-gray-700 mb-2">
            Your application has been submitted successfully
          </p>
          <p className="text-lg text-gray-600">
            We're setting up your Smartr365 platform now
          </p>
        </div>

        {/* Information Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="mb-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-blue-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  What happens next?
                </h3>
                <p className="text-gray-700 mb-4">
                  We'll send you an email{email ? ` at ${email}` : ''} when your platform is ready. This typically takes 15-30 minutes.
                </p>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                1
              </div>
              <div className="ml-4">
                <h4 className="font-semibold text-gray-900">Environment Setup</h4>
                <p className="text-gray-600 text-sm">We're creating your dedicated Smartr365 instance with all the features you need</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                2
              </div>
              <div className="ml-4">
                <h4 className="font-semibold text-gray-900">Configuration</h4>
                <p className="text-gray-600 text-sm">Your platform is being configured with your business details and preferences</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                3
              </div>
              <div className="ml-4">
                <h4 className="font-semibold text-gray-900">Email Notification</h4>
                <p className="text-gray-600 text-sm">You'll receive an email with your login credentials and platform URL</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                4
              </div>
              <div className="ml-4">
                <h4 className="font-semibold text-gray-900">Get Started</h4>
                <p className="text-gray-600 text-sm">Log in and start exploring Smartr365 with your team</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-4">
            Have questions or need assistance?
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:support@smartr365.com"
              className="inline-flex items-center justify-center px-6 py-3 bg-white border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email Support
            </a>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm text-gray-500 mt-8">
          You can safely close this page. We'll keep you updated via email.
        </p>
      </div>
    </div>
  );
};
